import { useStreamingReport } from '../../hooks/useStreamingReport';
import StreamingStatus from '../../components/StreamingStatus';
import React, { useEffect, useMemo } from 'react';
import { Button, Form, Select, ConfigProvider, message } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import dayjs from 'dayjs';
import dayOfYear from 'dayjs/plugin/dayOfYear';
import 'dayjs/locale/zh-cn';
import { SolarDay, SolarTime } from 'tyme4ts';
import { Solar } from 'lunar-typescript';
import { useIztro } from 'iztro-hook';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import '../../App.css';
import InviteCodeUnlock from '../../components/InviteCodeUnlock';
import BirthDateTimePicker from '../../components/BirthDateTimePicker';
import FourPillars from '../../components/FourPillars';
import AILoadingAnimation from '../../components/AILoadingAnimation';
import { CITY_OPTIONS, cityFilterOption, type City } from '../../data/cities';
import { calculateDaYunWithAge, type DaYunWithAge, ZIWEI_CONFIG, buildZiWeiData } from '../../utils';
import { useExportImage } from '../../hooks/useExportImage';

dayjs.locale('zh-cn');
dayjs.extend(dayOfYear);

const DEFAULT_ZIWEI_BIRTHDAY = '2000-1-1';

interface FormValues {
  birthDateTime: {
    date: dayjs.Dayjs;
    isLunar: boolean;
  };
  gender: 'male' | 'female';
  city?: string;
  longitude: number;
  latitude?: number;
  distinguishZiShi?: boolean;
  currentInterests?: string;
  parentConcerns?: string;
  childTraits?: string;
}

function toTrueSolarTimeWithMinutes(datetime: dayjs.Dayjs, longitude: number, timeZoneOffset: number = 8): dayjs.Dayjs {
  const n = datetime.dayOfYear();
  const B = (2 * Math.PI * (n - 81)) / 364;
  const eot = 9.87 * Math.sin(2 * B) - 7.53 * Math.cos(B) - 1.5 * Math.sin(B);
  const zoneLongitude = 15 * timeZoneOffset;
  const deltaLon = 4 * (longitude - zoneLongitude);
  const deltaMinutes = eot + deltaLon;
  return datetime.add(deltaMinutes, 'minute');
}

function hourToShiChenIndex(hour: number): number {
  return Math.floor((hour + 1) / 2);
}

export const TalentKidPage = () => {
  const [form] = Form.useForm();
  const reportRef = React.useRef<HTMLDivElement | null>(null);
  const { exportImage } = useExportImage('儿童天赋分析报告');

  const [fourPillars, setFourPillars] = React.useState<{ year: string; month: string; day: string; hour: string } | null>(null);
  const [daYun, setDaYun] = React.useState<DaYunWithAge[]>([]);
  const [lunarDateStr, setLunarDateStr] = React.useState<string>('');
  const [trueSolarTimeStr, setTrueSolarTimeStr] = React.useState<string>('');

  const [aiLoading, setAiLoading] = React.useState(false);
  const { run: runReportStream, streaming: reportStreaming, error: streamError, access: reportAccess, reportToken: streamReportToken } = useStreamingReport();
  const [talentAnswer, setTalentAnswer] = React.useState<string>('');
  const reportHasContent = Boolean(talentAnswer);
  const [reportToken, setReportToken] = React.useState('');

  const [ziWeiBirthday, setZiWeiBirthday] = React.useState<string>('');
  const [ziWeiBirthTime, setZiWeiBirthTime] = React.useState<number>(0);
  const [ziWeiGender, setZiWeiGender] = React.useState<'男' | '女'>('女');

  const { astrolabe } = useIztro({
    birthday: ziWeiBirthday || DEFAULT_ZIWEI_BIRTHDAY,
    birthTime: ziWeiBirthTime,
    gender: ziWeiGender,
    birthdayType: 'solar',
    fixLeap: true,
    lang: 'zh-CN',
    ...(ZIWEI_CONFIG as any),
  });

  console.log('daYun:', daYun);

  const ziWeiData = useMemo(() => {
    if (!astrolabe || !ziWeiBirthday || ziWeiBirthday === DEFAULT_ZIWEI_BIRTHDAY) {
      return null;
    }
    return buildZiWeiData(astrolabe, ziWeiBirthTime);
  }, [astrolabe, ziWeiBirthday, ziWeiBirthTime]);

  const cityOptions = React.useMemo(() => CITY_OPTIONS, []);

  const handleCityChange = (_cityName: string, option: any) => {
    const selectedCity = option.city as City;
    form.setFieldsValue({
      longitude: selectedCity.longitude,
      latitude: selectedCity.latitude,
    });
  };

  const performPaiPan = (birthInfo: {
    year?: number;
    month?: number;
    day?: number;
    hour?: number;
    minute?: number;
    solar?: string;
    longitude: number;
    gender: 'male' | 'female';
    distinguishZiShi?: boolean;
  }) => {
    let inputYear: number;
    let inputMonth: number;
    let inputDay: number;
    let inputHour: number;
    let inputMinute: number;

    if (birthInfo.solar) {
      const solarDate = dayjs(birthInfo.solar);
      inputYear = solarDate.year();
      inputMonth = solarDate.month() + 1;
      inputDay = solarDate.date();
      inputHour = solarDate.hour();
      inputMinute = solarDate.minute();
    } else {
      inputYear = birthInfo.year!;
      inputMonth = birthInfo.month!;
      inputDay = birthInfo.day!;
      inputHour = birthInfo.hour!;
      inputMinute = birthInfo.minute!;
    }

    const { longitude, gender, distinguishZiShi = false } = birthInfo;
    const standard = dayjs(
      `${inputYear}-${inputMonth.toString().padStart(2, '0')}-${inputDay
        .toString()
        .padStart(2, '0')} ${inputHour.toString().padStart(2, '0')}:${inputMinute
          .toString()
          .padStart(2, '0')}:00`,
    );
    const trueSolar = toTrueSolarTimeWithMinutes(standard, longitude, 8);

    let standardYear = standard.year();
    let standardMonth = standard.month() + 1;
    let standardDay = standard.date();
    const standardHour = standard.hour();
    const standardMinute = standard.minute();

    const isInZiShi = standardHour === 23 || standardHour === 0;
    if (isInZiShi && !distinguishZiShi && standardHour === 23) {
      const nextDay = standard.add(1, 'day');
      standardYear = nextDay.year();
      standardMonth = nextDay.month() + 1;
      standardDay = nextDay.date();
    }

    const year = trueSolar.year();
    const month = trueSolar.month() + 1;
    const day = trueSolar.date();
    const trueSolarHour = trueSolar.hour();
    const trueSolarMinute = trueSolar.minute();

    setTrueSolarTimeStr(trueSolar.format('YYYY年MM月DD日 HH:mm'));

    const solarDayStandard = SolarDay.fromYmd(standardYear, standardMonth, standardDay);
    const lunarDayStandard = solarDayStandard.getLunarDay();
    const solarObj = Solar.fromDate(standard.toDate());
    const lunarObj = solarObj.getLunar();

    const solarHourTrue = SolarTime.fromYmdHms(year, month, day, trueSolarHour, trueSolarMinute, 0);
    const lunarHourTrue = solarHourTrue.getLunarHour();

    const sixtyCycleYear = lunarObj.getYearInGanZhiExact();
    let sixtyCycleMonth = lunarDayStandard.getMonthSixtyCycle().getName();
    const sixtyCycleDay = lunarDayStandard.getSixtyCycle().getName();
    const sixtyCycleHour = lunarHourTrue.getSixtyCycle().getName();

    try {
      const currentJieQi = lunarObj.getCurrentJieQi();

      if (currentJieQi && currentJieQi.isJie()) {
        const jieQiSolar = currentJieQi.getSolar();
        const birthTimestamp = standard.valueOf();
        const jieQiTimestamp = new Date(
          jieQiSolar.getYear(),
          jieQiSolar.getMonth() - 1,
          jieQiSolar.getDay(),
          jieQiSolar.getHour(),
          jieQiSolar.getMinute(),
          jieQiSolar.getSecond(),
        ).getTime();

        if (birthTimestamp < jieQiTimestamp) {
          const prevJieQi = lunarObj.getPrevJieQi();
          if (prevJieQi) {
            const prevJieQiSolar = prevJieQi.getSolar();
            const prevSolar = Solar.fromYmd(
              prevJieQiSolar.getYear(),
              prevJieQiSolar.getMonth(),
              prevJieQiSolar.getDay(),
            );
            sixtyCycleMonth = prevSolar.getLunar().getMonthInGanZhi();
          }
        }
      }
    } catch (error) {
      console.error('月柱节气精确判断失败，使用默认值:', error);
    }

    setLunarDateStr(
      `${lunarObj.getYearInGanZhiExact()}(${lunarObj.getYearShengXiaoExact()})年 ${lunarObj.getMonthInChinese()}月${lunarObj.getDayInChinese()} ${lunarHourTrue
        .getSixtyCycle()
        .getName()}时`,
    );

    setFourPillars({ year: sixtyCycleYear, month: sixtyCycleMonth, day: sixtyCycleDay, hour: sixtyCycleHour });

    const daYunList = calculateDaYunWithAge(
      standardYear,
      standardMonth,
      standardDay,
      standardHour,
      standardMinute,
      sixtyCycleYear,
      sixtyCycleMonth,
      gender,
      8,
    );
    setDaYun(daYunList);

    let validHour = Math.floor(trueSolarHour);
    if (validHour < 0) validHour = 0;
    if (validHour > 23) validHour = 23;

    const birthdayStr = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    const shiChenIndex = hourToShiChenIndex(validHour);

    setZiWeiBirthday(birthdayStr);
    setZiWeiBirthTime(shiChenIndex);
    setZiWeiGender(gender === 'male' ? '男' : '女');
  };

  const onFinish = async (values: FormValues) => {
    const { birthDateTime, longitude, gender, distinguishZiShi } = values;
    if (!birthDateTime) {
      message.error('请选择出生日期和时间');
      return;
    }

    const { date } = birthDateTime;
    const longitudeNum = Number(longitude);
    if (Number.isNaN(longitudeNum)) {
      message.error('请输入有效的经度，例如 116.4');
      return;
    }

    const standard = date.second(0).millisecond(0);
    performPaiPan({
      year: standard.year(),
      month: standard.month() + 1,
      day: standard.date(),
      hour: standard.hour(),
      minute: standard.minute(),
      longitude: longitudeNum,
      gender,
      distinguishZiShi: distinguishZiShi || false,
    });

    setTalentAnswer('');
    setReportToken('');
  };

  const generateTalentReport = async () => {
    if (!fourPillars) {
      message.error('请先完成排盘');
      return;
    }

    const values = form.getFieldsValue();
    const { birthDateTime, city, gender, longitude, latitude, currentInterests, parentConcerns, childTraits } = values;

    const birthInfo = {
      solar: birthDateTime.date.format('YYYY-MM-DD HH:mm'),
      city,
      longitude,
      latitude,
      gender,
    };

    setAiLoading(true);
    setTalentAnswer('');
    setReportToken('');
    try {
      const resp = await runReportStream('/api/talent-kid', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fourPillars,
          gender,
          birthInfo,
          ziWeiData,
          currentInterests: currentInterests || '',
          parentConcerns: parentConcerns || '',
          childTraits: childTraits || '',
        }),
      }, (text) => { setTalentAnswer(text); setAiLoading(false); });

      if (!resp.ok) {
        let errorMessage = '天赋分析报告生成失败';
        try {
          const errorData = await resp.json();
          errorMessage = errorData.error || errorData.details || errorMessage;
        } catch {
          // ignore json parse error
        }
        throw new Error(errorMessage);
      }

      const data = await resp.json();
      setTalentAnswer((data.access === 'full' ? data.answer : data.previewAnswer || data.answer) || '暂无报告内容');
      setReportToken(data.reportToken || '');
      message.success('儿童天赋报告已生成');
    } catch (error: any) {
      console.error(error);
      message.error(error.message || '生成失败，请稍后重试');
    } finally {
      setAiLoading(false);
    }
  };

  const onReset = () => {
    form.resetFields();
    setFourPillars(null);
    setDaYun([]);
    setLunarDateStr('');
    setTrueSolarTimeStr('');
    setTalentAnswer('');
    setReportToken('');
  };

  useEffect(() => {
    form.setFieldsValue({
      birthDateTime: { date: dayjs(), isLunar: false },
      gender: '',
    });
  }, [form]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (reportRef.current) {
        reportRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [reportHasContent]);

  return (
    <ConfigProvider locale={zhCN}>
      <AILoadingAnimation visible={aiLoading} />
      <div className="app-container">
        <div className="app-layout">
          <div className="center-content">
            <div style={{ width: '100%', maxWidth: '900px' }}>
              <div className="form-card">
                <h1 className="form-title">儿童天赋启蒙</h1>
                <h2 className="hero-subtitle">发现孩子的天赋密码，找到最适合的兴趣班</h2>
                <Form form={form} name="talent-kid-form" onFinish={onFinish} layout="vertical">
                  <Form.Item
                    name="birthDateTime"
                    label="孩子出生时间"
                    rules={[{ required: true, message: '请选择出生时间' }]}
                  >
                    <BirthDateTimePicker />
                  </Form.Item>

                  <Form.Item
                    name="gender"
                    label="性别"
                    rules={[{ required: true, message: '请选择性别' }]}
                  >
                    <Select
                      placeholder="请选择性别"
                      options={[
                        { value: 'male', label: '男' },
                        { value: 'female', label: '女' },
                      ]}
                    />
                  </Form.Item>

                  <Form.Item
                    name="city"
                    label="出生城市"
                    rules={[{ required: true, message: '请选择出生城市' }]}
                  >
                    <Select
                      showSearch
                      placeholder="请选择城市"
                      options={cityOptions}
                      onChange={handleCityChange}
                      optionFilterProp="searchText"
                      filterOption={cityFilterOption}
                    />
                  </Form.Item>

                  {/* 隐藏字段：经度值由城市选择自动填充 */}
                  <Form.Item name="longitude" hidden>
                    <input type="number" />
                  </Form.Item>

                  <Form.Item
                    name="currentInterests"
                    label="目前在上的兴趣班"
                    tooltip="描述孩子目前在上的兴趣班，例如：钢琴、舞蹈、绘画、游泳等"
                  >
                    <input
                      type="text"
                      placeholder="例如：钢琴、舞蹈、英语、画画"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d9d9d9',
                        borderRadius: '6px',
                        fontSize: '14px',
                        outline: 'none',
                        transition: 'border-color 0.3s'
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = '#d4af6a'}
                      onBlur={(e) => e.currentTarget.style.borderColor = '#d9d9d9'}
                    />
                  </Form.Item>

                  <Form.Item
                    name="childTraits"
                    label="孩子的性格特点"
                    tooltip="描述孩子的性格和行为特点"
                  >
                    <textarea
                      placeholder="例如：特别好动，坐不住；或者很安静，喜欢一个人画画；或者特别有主见，不听指挥..."
                      rows={2}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d9d9d9',
                        borderRadius: '6px',
                        fontSize: '14px',
                        outline: 'none',
                        resize: 'vertical',
                        minHeight: '60px',
                        transition: 'border-color 0.3s'
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = '#d4af6a'}
                      onBlur={(e) => e.currentTarget.style.borderColor = '#d9d9d9'}
                    />
                  </Form.Item>

                  <Form.Item
                    name="parentConcerns"
                    label="家长的困惑"
                    tooltip="描述您在教育孩子过程中的困惑或担忧"
                  >
                    <textarea
                      placeholder="例如：孩子上了很多兴趣班但都不专注，不知道该怎么选；或者孩子特别好动，是不是有多动症；或者孩子偏科严重，不知道要不要纠正..."
                      rows={3}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d9d9d9',
                        borderRadius: '6px',
                        fontSize: '14px',
                        outline: 'none',
                        resize: 'vertical',
                        minHeight: '80px',
                        transition: 'border-color 0.3s'
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = '#d4af6a'}
                      onBlur={(e) => e.currentTarget.style.borderColor = '#d9d9d9'}
                    />
                  </Form.Item>

                  <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                    <Button type="primary" htmlType="submit" className="submit-button">
                      开始排盘
                    </Button>
                    <Button htmlType="button" onClick={onReset} className="reset-button">
                      重置
                    </Button>
                  </div>
                </Form>
              </div>

              {fourPillars && (
                <div className="result-card" style={{ marginTop: '24px' }}>
                  <h2 className="result-title">排盘结果</h2>
                  <div style={{ textAlign: 'center', marginBottom: 20, color: '#666' }}>
                    <p>公历（北京时间）：{form.getFieldValue('birthDateTime')?.date?.format('YYYY年MM月DD日 HH:mm')}</p>
                    <p style={{ color: '#d4af6a', fontWeight: 500, fontSize: '14px', margin: '8px 0' }}>真太阳时：{trueSolarTimeStr}</p>
                    <p>农历：{lunarDateStr}</p>
                  </div>
                  <FourPillars {...fourPillars} />

                  <div style={{ marginTop: '24px', textAlign: 'center' }}>
                    <Button type="primary" size="large" className="submit-button" disabled={reportStreaming} onClick={generateTalentReport}>
                      生成天赋分析报告
                    </Button>
                  </div>
                </div>
              )}

              <StreamingStatus active={reportStreaming && Boolean(talentAnswer)} error={streamError} />
              {talentAnswer && (
                <div ref={reportRef} className="result-card" style={{ marginTop: '24px', marginBottom: '24px' }}>
                  {(reportToken || (reportStreaming && reportAccess === 'preview')) && <InviteCodeUnlock key={reportToken || streamReportToken} product="talent-kid" reportToken={reportToken || streamReportToken}
                    onUnlocked={(fullAnswer) => { if (fullAnswer) setTalentAnswer(fullAnswer); setReportToken(''); }} />}
                  <div className="talent-kid-report-content">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                      <h2 className="result-title" style={{ margin: 0 }}>儿童天赋分析报告</h2>
                      <Button
                        type="primary"
                        disabled={reportStreaming || Boolean(streamError)} onClick={() => exportImage('.talent-kid-report-content')}
                        style={{
                          background: 'linear-gradient(135deg, #d4af6a 0%, #b8935d 100%)',
                          border: 'none',
                          fontWeight: 600
                        }}
                      >
                        🖼️ 保存为图片
                      </Button>
                    </div>
                    
                    {/* 保存提示 */}
                    <div style={{
                      margin: '0 0 20px',
                      padding: '14px 18px',
                      background: 'linear-gradient(135deg, rgba(212, 175, 106, 0.15) 0%, rgba(212, 175, 106, 0.08) 100%)',
                      borderRadius: '10px',
                      border: '1px solid rgba(212, 175, 106, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <div style={{ fontSize: '24px' }}>💡</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '15px', fontWeight: 600, color: '#d4af6a', marginBottom: '4px' }}>
                          报告内容较长，建议先保存再慢慢品读
                        </div>
                        <div style={{ fontSize: '13px', color: '#666' }}>
                          点击"保存为图片"按钮，将报告保存到相册，方便随时查看
                        </div>
                      </div>
                    </div>
                    
                    <div className="markdown-content" style={{ color: '#2f2f2f' }}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{talentAnswer}</ReactMarkdown>
                    </div>
                    
                    {/* 底部保存按钮 */}
                    <div style={{
                      marginTop: '40px',
                      padding: '32px 24px',
                      background: 'linear-gradient(135deg, rgba(212, 175, 106, 0.08) 0%, rgba(212, 175, 106, 0.03) 100%)',
                      borderRadius: '16px',
                      border: '2px solid rgba(212, 175, 106, 0.2)',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '32px', marginBottom: '12px' }}>✨</div>
                      <h3 style={{
                        fontSize: '18px',
                        fontWeight: 600,
                        color: '#333',
                        marginBottom: '8px'
                      }}>
                        愿每个孩子都能被看见
                      </h3>
                      <p style={{
                        fontSize: '14px',
                        color: '#666',
                        lineHeight: '1.7',
                        marginBottom: '20px'
                      }}>
                        天赋不是培养出来的，而是发现出来的。<br />
                        愿这份报告帮助您看见孩子原本就有的光芒。
                      </p>
                      <Button
                        type="primary"
                        size="large"
                        disabled={reportStreaming || Boolean(streamError)} onClick={() => exportImage('.talent-kid-report-content')}
                        style={{
                          background: 'linear-gradient(135deg, #d4af6a 0%, #b8935d 100%)',
                          border: 'none',
                          height: '48px',
                          padding: '0 40px',
                          fontSize: '16px',
                          fontWeight: 600,
                          boxShadow: '0 6px 20px rgba(212, 175, 106, 0.3)'
                        }}
                      >
                        📸 立即保存长图
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </ConfigProvider>
  );
};

export default TalentKidPage;
